import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Typography,
  Paper,
  backdropClasses,
} from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { apiUrlPrefix } from '../../config';
import { useQueryClient } from '@tanstack/react-query';
import useStore from "./state-files/zustondStoreStates";
import useMessagesStore from './state-files/useMessagesStore';
import extractChatMessages from './helper-methods/fetchAndRefreshMessages';
import { GoogleLogin } from '@react-oauth/google';
import { AlignCenter } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { bgcolor, Box } from "@mui/system";



const CreateAccountWidget = ({ setIsCreatingAccount, closeMenu }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [data, setData] = useState(null);
  const queryClient = useQueryClient(); // Access React Query's client
  const setDiscussions = useStore((state) => state.setDiscussions); //In the even multiple accounts using the same browser
  const { communities, setCommunities, greenButtonSX } = useStore(); // Use zustand store
  const { setMessageList, setIsLoggedIn } = useMessagesStore();
  const navigate = useNavigate(); // Standard web equivalent of useRouter

  const fetchChatMessages = async () => {
    await setMessageList(extractChatMessages('69769ec2f601532a29c0a0a3', ''));
  };

  const handleClose = () => {
    formik.setFieldValue('email', '');
    formik.setFieldValue('name', '');
    formik.setFieldValue('password', '');
    setError(null);
    setOpen(false);
    // FIX 1: When the user cancels, close the sidebar menu too
    if (closeMenu) closeMenu();
  };

  const validationSchema = Yup.object({
    name: Yup.string().required("Name is required"),
    email: Yup.string().email("Invalid email address").required("Email is required"),
    password: Yup.string().min(8, "Password must be at least 8 characters long").required("Password is required"),
  });

  const formik = useFormik({
    initialValues: { name: "", email: "", password: "" },
    validationSchema,
    validateOnChange: false, // Only validate when the user clicks 'Create' or blurs the field
    validateOnBlur: true,
    onSubmit: (values) => {
      const apiUrl = apiUrlPrefix + "MongoDb/AddUser";
      const requestBody = {
        Id: "", // Let the backend handle Id generation if needed
        Name: values.name,
        Email: values.email,
        Password: values.password,
      };

      setLoading(true); // Start loading
      setError(null); // Clear previous errors

      fetch(apiUrl, {
        method: "POST",
        credentials: 'include',
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      })
        .then(async (response) => {
          if (!response.ok) {
            const errorMessage = await response.text();
            if (errorMessage) {
              setErrorMessage(errorMessage);
              return;
            }
            setErrorMessage(errorMessage);
            return;
          }
          return response.json();
        })
        .then(async (data) => {
          // Cache user info after successful creation
          queryClient.setQueryData(['user'], {
            email: values.email, // Use the form email here
            username: values.name, // Optional: add other user-related fields
            token: data.token,
          });

          localStorage.setItem('accessToken', data.userInfo.accessToken);
          localStorage.setItem('userId', data.userInfo.id);
          localStorage.setItem('username', data.userInfo.name);
          localStorage.setItem('profilePicture', '');
          formik.setFieldValue('email', '');
          formik.setFieldValue('name', '');
          formik.setFieldValue('password', '');

          navigate('/chat');
          setDiscussions();
          await fetchChatMessages();
          setIsLoggedIn(true); //Tells the server the user is logged in
          setData(data); // Store the server response
          setLoading(false); // End loading
          handleClose(); // Close the dialog
        })
        .catch((error) => {
          if (error.message.includes("Email")) {
            formik.setFieldError("email", error.message);
          } else {
            setError(error.message);
          }
          setLoading(false);
        });
    },
  });

  return (
    <Box sx={{
      height: '100%',
      bgcolor: 'white',
      borderRadius: 1,
      boxShadow: 1,
      paddingLeft: 2,
      paddingRight: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      <Typography id="loginButton" variant="h5" sx={{ textAlign: 'center' }}
        color="none" style={{ boxShadow: "none" }}>
        Create Account
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <label style={{ color: "red" }}>{errorMessage}</label>
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          type="text"
          fullWidth
          variant="outlined"
          name="name"
          value={formik.values.name}
          onKeyDown={(e) => {
            // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
            e.stopPropagation();
          }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.name && Boolean(formik.errors.name)}
          helperText={formik.touched.name && formik.errors.name}
        />

        <Typography style={{ textAlign: 'center' }}><b>Sign in with Google for your email</b></Typography>
        <TextField
          margin="dense"
          label="Email"
          type="email"
          fullWidth
          variant="outlined"
          name="email"
          disabled={true}
          value={formik.values.email}
          onKeyDown={(e) => {
            // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
            e.stopPropagation();
          }}
          onBlur={formik.handleBlur}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
        {/* Google login */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          marginTop: '20px'
        }}>
          <GoogleLogin
            onSuccess={async (credentialResponse) => {

              // On web, the 'credential' IS the idToken
              const idToken = credentialResponse.credential;


              const result = await fetch(apiUrlPrefix + 'MongoDb/api/auth/google', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: idToken })
              });

              if (result.status === 200) {
                var email = await result.json();
                formik.setFieldValue('email', email.email);
              }
            }}
            onError={() => {
              console.log('Login Failed');
            }}
          />
        </div>

        <p></p>

        {/* End Google login */}

        <TextField
          margin="dense"
          label="Password"
          type="password"
          fullWidth
          variant="outlined"
          name="password"
          value={formik.values.password}
          onKeyDown={(e) => {
            // This stops the 'a', 'd', 'h', 'l' from reaching your global shortcuts
            e.stopPropagation();
          }}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
        />
        <Box sx={{ textAlign: 'center' }}>
          <Button type="submit" color="primary" disabled={loading} sx={[greenButtonSX, { padding: 0.5, width: 0.75, borderRadius: 4, marginBottom: 1, marginTop: 1 }]}>
            {loading ? "Creating..." : "Create Account"}
          </Button>
          <br />
          <Button onClick={() => setIsCreatingAccount(false)} color="primary" sx={[greenButtonSX, { padding: 0.5, width: 0.75, borderRadius: 4 }]}>
            Login
          </Button>
        </Box>
      </form>
      {error && <div style={{ color: "red" }}>{error}</div>}
    </Box>
  );
};

export default CreateAccountWidget;
